import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { AlertTriangle, Clock, CreditCard, X } from 'lucide-react';

export const SubscriptionBanner = () => {
  const { subscriptionStatus, user } = useAuth();
  const navigate = useNavigate();
  const [dismissed, setDismissed] = useState(false);

  if (!subscriptionStatus || subscriptionStatus.is_super_admin) return null;
  if (user?.role === 'super_admin' || user?.role === 'guest') return null;
  if (subscriptionStatus.status === 'pending') return null;

  const { needs_payment, show_expiry_warning, days_remaining, is_trial, trial_expired, billing_expired } = subscriptionStatus;

  // Expired modal (blocks usage)
  if (needs_payment) {
    return (
      <Dialog open={true} onOpenChange={() => {}}>
        <DialogContent className="max-w-md [&>button]:hidden" onPointerDownOutside={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              {trial_expired ? 'Free Trial Expired' : 'Subscription Expired'}
            </DialogTitle>
            <DialogDescription className="sr-only">Your subscription has expired</DialogDescription>
          </DialogHeader>
          <div className="space-y-4" data-testid="subscription-expired-modal">
            <p className="text-slate-600">
              {trial_expired
                ? 'Your free trial has ended. To continue using iHostPMS, please subscribe to a plan.'
                : 'Your subscription has expired. Please renew to continue managing your properties.'}
            </p>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <p className="text-sm text-amber-800 font-medium">Save with annual billing — get 2 months free!</p>
            </div>
            <Button
              onClick={() => navigate('/subscription')}
              className="w-full bg-[#FF385C] hover:bg-[#E31C5F] text-white rounded-xl"
              data-testid="subscribe-now-btn"
            >
              <CreditCard className="w-4 h-4 mr-2" /> Subscribe Now
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // Warning banner (7 days before expiry)
  if (show_expiry_warning && !dismissed) {
    return (
      <div className="mx-4 mb-4 bg-amber-50 border border-amber-200 rounded-xl p-3 flex items-center justify-between" data-testid="subscription-expiry-warning">
        <div className="flex items-center gap-3">
          <Clock className="w-5 h-5 text-amber-600 flex-shrink-0" />
          <div>
            <p className="text-sm font-medium text-amber-800">
              {is_trial ? 'Free trial' : 'Subscription'} expires in {days_remaining} day{days_remaining !== 1 ? 's' : ''}
            </p>
            <p className="text-xs text-amber-600">Renew now to avoid interruption. Annual plans save you 2 months!</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <Button size="sm" onClick={() => navigate('/subscription')} className="bg-amber-600 hover:bg-amber-700 text-white rounded-lg text-xs" data-testid="renew-now-btn">
            Renew Now
          </Button>
          <button onClick={() => setDismissed(true)} className="text-amber-400 hover:text-amber-600">
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    );
  }

  return null;
};
